/**
 * Reference composition: the default module card, expressed as a scene.
 * This is the proof that a real widget's visual can be built from the model
 * and rendered headless. The live `arch-scene-module` widget builds the
 * exact same scene and paints it via the HTML backend.
 *
 * Card = rounded box (160° gradient bg, soft accent border, drop shadow,
 * bright accent stripe along the top, optional corner badge) containing a
 * column:
 *   header row [ icon glyph, name (bold, grows, ellipsized) ]
 *   subheader (muted)
 *   footer (muted, smaller)
 *   chips row [ pill, pill ] (key: value labels)
 */
import { box, richtext, slot, text, type BoxNode, type FontSpec, type Node, type Overlay, type TextRun } from "./model.js";
import { alpha, defaultTokens, type Tokens } from "./tokens.js";
import { composeMarkdown } from "./markdown.js";

export interface ModuleChip {
  readonly key?: string;
  readonly value: string;
}

export interface ModuleCardInput {
  readonly name: string;
  /** §4.14 — the name was derived from the module's type (unnamed module).
   *  Renders the name italic + muted so it reads as "not spelled out". */
  readonly unnamed?: boolean;
  readonly subheader?: string;
  readonly footer?: string;
  /** Body prose (inline markdown). Wraps + grows to fill the card. */
  readonly description?: string;
  /** Inner-SVG for the header icon, drawn against a 24x24 viewBox. */
  readonly iconBody?: string;
  readonly accent?: string;
  readonly dashed?: boolean;
  /** Custom background (CSS color/gradient) overriding the default gradient.
   *  HTML honors any CSS value; SVG renders solid colors (not images). */
  readonly bg?: string;
  /** Key:value label pills rendered in the body. */
  readonly chips?: readonly ModuleChip[];
  /** Short corner tag (e.g. a cloud provider), pinned top-right. */
  readonly badge?: string;
  /** Badge accent; defaults to the card accent. */
  readonly badgeColor?: string;
  /** Subspace mode: body becomes a host-filled portal (the nested space);
   *  description / chips / footer are omitted (the renderer overlays them). */
  readonly subspace?: boolean;
  /** Connection-point dots on the card edges: in→left, out→right, plus top/bottom. */
  readonly anchors?: ReadonlyArray<"in" | "out" | "top" | "bottom">;
  /** Body outline: rounded rectangle (default) or ellipse. */
  readonly shape?: "rect" | "ellipse";
}

const ANCHOR_EDGE = { in: "left", out: "right", top: "top", bottom: "bottom" } as const;

function chip(c: ModuleChip, font: FontSpec, t: Tokens): BoxNode {
  // Mirrors arch-module's `.chip` (no border, accent-tinted bg, two-tone
  // KEY/value), but a rounded rectangle instead of the pill stadium.
  const keyFont: FontSpec = { ...font, size: font.size * 0.85, weight: 600 };
  const children = [
    ...(c.key
      ? [text(c.key.toUpperCase(), { font: keyFont, color: t.fgMuted, align: "start" })]
      : []),
    text(c.value, { font, color: t.fgSoft, align: "start", ellipsis: true }),
  ];
  const s = t.fontSize / 14;
  return box({
    direction: "row",
    align: "center",
    gap: 4 * s,
    padding: [1 * s, 5 * s, 1 * s, 5 * s],
    style: {
      fill: alpha(t.accent, 0.16), // translucent — sits lighter over the card
      radius: t.radiusSm,
    },
    children,
  });
}

function badgeNode(label: string, color: string, t: Tokens): Node {
  const font: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.62, weight: 700 };
  const s = t.fontSize / 14;
  return box({
    direction: "row",
    align: "center",
    padding: [2 * s, t.space2, 2 * s, t.space2],
    reveal: "compact",
    style: {
      fill: alpha(color, 0.22), // translucent badge
      stroke: alpha(color, 0.5),
      strokeWidth: t.borderThin,
      // Hug the card's top-right corner: round only top-right (card radius)
      // and bottom-left (small); top-left + bottom-right stay flush.
      radius: [0, t.radiusMd, 0, t.radiusSm],
    },
    children: [text(label.toUpperCase(), { font, color, align: "center" })],
  });
}

/** Fallback card dims the dot-zoom swell caps against when no dims given. */
const REF_HEIGHT = 170;
const REF_WIDTH = 240;

/** A described container's description counts as a CAPTION (a line or two
 *  above the subspace, full width) up to this length; longer prose earns the
 *  side column instead. ~2 wrapped lines on a typical container card. */
const CAPTION_DESC_MAX = 120;

/** Fraction of the card's inner width the long-description column takes.
 *  Baked to an explicit px width so live CSS flex and the layout solver
 *  place the subspace slot identically — the portal grows into the rest. */
const DESC_COLUMN_FRAC = 0.4;

/**
 * Legacy dot-zoom title fill, computed numerically from the node's model dims.
 *
 * The old widget grew the title as `clamp(8, min(base + 22cqh·(1−reveal),
 * 16cqw), 96)` so it swelled to fill the card as the body collapsed at far
 * zoom. The scene composes at fixed model dims and CSS-zooms, so cqh/cqw are
 * constants we bake here: `base` at full zoom, growing to `filled` (a bandShrink
 * added as the compact band → 0). Width-capped so a long name doesn't overflow.
 */
export function titleBandSwell(base: number, w: number, h: number): number {
  const filled = Math.max(8, Math.min(base + 0.22 * h, 0.16 * w, 96));
  return Math.max(0, filled - base);
}

export interface CardDims {
  readonly width: number;
  readonly height: number;
}

export function composeModuleCard(
  input: ModuleCardInput,
  baseTokens: Tokens = defaultTokens(input.accent),
  dims?: CardDims,
): BoxNode {
  // Uniform type scale: fonts, icons, and spacing come from the tokens alone —
  // a bigger node (a `system` card vs a default module) buys ROOM for content,
  // never magnification. Only the dot-zoom band swell reads the dims.
  const t = baseTokens;
  const titleFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 1.15, weight: 700 };
  const subFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.9, weight: 500 };
  const footFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.78, weight: 500 };
  const chipFont: FontSpec = { family: t.fontFamily, size: t.fontSize * 0.72, weight: 600 };

  // The icon is the leading "word" of the name — it flows with the text,
  // wraps + centers together (like the HTML widgets), instead of sitting in a
  // fixed slot beside it. The name is pre-split into words by the widget.
  const nameRuns: TextRun[] = [];
  if (input.iconBody) {
    const iconSize = titleFont.size * 1.05;
    nameRuns.push({ text: "", glyph: { body: input.iconBody, viewW: 24, viewH: 24, color: t.accent, width: iconSize, height: iconSize } });
  }
  nameRuns.push({ text: input.name, italic: input.unnamed || undefined });
  const nameNode = richtext(nameRuns, {
    font: titleFont,
    color: input.unnamed ? alpha(t.fg, 0.75) : t.fg,
    align: "center", wrap: true, maxLines: 3,
    // Swell to fill the card at dot zoom (legacy width-capped cqh fill), baked
    // from model dims — far stronger than the old flat 0.5×font.
    bandShrink: titleBandSwell(titleFont.size, dims?.width ?? REF_WIDTH, dims?.height ?? REF_HEIGHT),
  });
  // Header = name + optional subheader, centered (legacy DOM: both live inside
  // `.header`). Grows to fill the card when the body collapses at dot zoom;
  // justify center keeps the (possibly multi-line) name vertically centered.
  // Keeping the subheader here (not in the body group) means subspace mode's
  // body is JUST the portal, so the nested space fills the whole body region.
  const headerChildren: Node[] = [nameNode];
  if (input.subheader) {
    headerChildren.push(text(input.subheader, { font: subFont, color: t.fgMuted, align: "center", ellipsis: true }));
  }
  const header = box({ direction: "column", grow: 1, justify: "center", children: headerChildren });

  // Body content lives in a band-gated group: collapses at far zoom (header
  // only), grows + reveals as the user zooms in. Full detail when no board
  // drives the band (SVG export, standalone HTML).
  // Drop a leading `# heading` line (it duplicates the module name shown in
  // the header) before composing the description block tree.
  const desc = (input.description ?? "").replace(/^\s*#{1,6}\s+[^\n]*\n+/, "").trim();
  const body: Node[] = [];
  if (input.subspace) {
    // Subspace mode: the body is a portal the renderer fills with the nested
    // space. A described container keeps its prose two ways: a caption-length
    // description stacks as a line above the portal (the subspace keeps the
    // full card width); longer or structured prose (any explicit line break)
    // earns a left column (1:2, the subspace keeps the lion's share) so it
    // doesn't squash the nested space from above. Chips / footer stay omitted
    // either way.
    const portal = slot({ part: "subspace", grow: 2 });
    if (desc) {
      const md = composeMarkdown(desc, t, { fontSize: subFont.size, color: t.fgSoft });
      const caption = desc.length <= CAPTION_DESC_MAX && !desc.includes("\n");
      if (caption) {
        body.push(md, { ...portal, grow: 1 });
      } else {
        const colW = Math.round(((dims?.width ?? REF_WIDTH) - 2 * t.space3) * DESC_COLUMN_FRAC);
        body.push(
          box({ direction: "row", gap: t.space2, grow: 1, children: [{ ...md, width: colW }, portal] }),
        );
      }
    } else {
      body.push({ ...portal, grow: 1 });
    }
  } else {
    // Full markdown body.
    if (desc) {
      const md = composeMarkdown(desc, t, { fontSize: subFont.size, color: t.fgSoft });
      body.push({ ...md, grow: 1 });
    }
    if (input.chips?.length) {
      body.push(box({ direction: "row", gap: t.space2, height: chipFont.size * 1.9, children: input.chips.map((c) => chip(c, chipFont, t)) }));
    }
    if (input.footer) {
      body.push(text(input.footer, { font: footFont, color: t.fgMuted, align: "start", ellipsis: true }));
    }
  }

  // Body swallows the free space (grow 999 vs the header's grow 1 — the legacy
  // `flex: calc(999×band) 0 0` contract) so the header stays content-sized
  // instead of eating a fixed fraction of the node. At dot zoom the band
  // collapses the body to 0 and the header's grow 1 fills + centers the name.
  const bodyGroup = box({
    direction: "column", gap: t.space1, grow: 999,
    growBand: "compact", reveal: "compact", children: body,
  });
  return wrapCard([header, bodyGroup], input, t);
}

/** Wrap the body rows in the card frame: gradient/border/shadow, top accent
 *  stripe overlay, and an optional corner badge. */
function wrapCard(rows: Node[], input: ModuleCardInput, t: Tokens): BoxNode {
  const s = t.fontSize / 14; // proportional scale (1 at the default size)
  const ellipse = input.shape === "ellipse";
  const overlays: Overlay[] = ellipse
    ? [] // an ellipse has no straight top edge for the stripe
    : [
        {
          at: "top",
          stretchX: true,
          node: box({ direction: "row", height: 2 * s, style: { fill: t.accent, opacity: 0.7 }, children: [] }),
        },
      ];
  if (input.badge) {
    overlays.push({ at: "top-right", node: badgeNode(input.badge, input.badgeColor ?? t.accent, t) });
  }
  for (const a of input.anchors ?? []) {
    overlays.push({
      at: ANCHOR_EDGE[a],
      inset: 2 * s,
      node: box({ direction: "row", width: 8 * s, height: 8 * s, reveal: "compact", style: { fill: t.accent, radius: t.radiusPill }, children: [] }),
    });
  }

  // An ellipse loses its corners — pad the content inward so it clears the curve.
  const padX = ellipse ? t.space3 * 2.2 : t.space3;
  const padY = ellipse ? t.space3 * 1.4 : t.space2;
  return box({
    direction: "column",
    gap: t.space1,
    padding: [padY, padX, padY, padX],
    align: ellipse ? "center" : undefined,
    style: {
      fill: input.bg && !input.dashed ? input.bg : undefined,
      gradient: input.dashed || input.bg ? undefined : { angle: 160, from: t.bgElev2, to: t.bgElev1 },
      stroke: input.dashed ? t.accent : alpha(t.accent, 0.38),
      strokeWidth: t.borderThin,
      radius: t.radiusMd,
      shape: ellipse ? "ellipse" : undefined,
      dashed: input.dashed,
      shadow: input.dashed ? undefined : { dx: 0, dy: 6 * s, blur: 20 * s, spread: -8 * s, color: "rgba(0,0,0,0.5)" },
    },
    overlays,
    children: rows,
  });
}
